// Aggregate unread + mention counters across the user's whole inbox, used
// by the Electron dock badge and the browser tab title. Sums across every
// non-archived, non-muted channel the user belongs to, with the currently-
// selected channel excluded so opening a channel immediately drops its
// contribution to the badge (matching the per-row Sidebar behavior at
// Sidebar.tsx:240).

import { useMemo } from "react";
import { useSidebarTree } from "./sidebar-tree";

interface ChannelLike {
  id: string;
  is_archived?: boolean | null;
}

export interface GlobalUnreadCounts {
  totalUnread: number;
  totalMentions: number;
  channelCount: number;
}

const ZERO_COUNTS: GlobalUnreadCounts = {
  totalUnread: 0,
  totalMentions: 0,
  channelCount: 0,
};

export function aggregateGlobalUnreadCounts(
  channelMap: ReadonlyMap<string, ChannelLike>,
  mutedSet: ReadonlySet<string>,
  unreadCountMap: ReadonlyMap<string, number>,
  mentionCountMap: ReadonlyMap<string, number>,
  selectedChannel: string | null,
): GlobalUnreadCounts {
  let totalUnread = 0;
  let totalMentions = 0;
  let channelCount = 0;
  for (const ch of channelMap.values()) {
    if (ch.is_archived) continue;
    if (mutedSet.has(ch.id)) continue;
    if (ch.id === selectedChannel) continue;
    const unread = unreadCountMap.get(ch.id) ?? 0;
    const mentions = mentionCountMap.get(ch.id) ?? 0;
    if (unread > 0) channelCount += 1;
    totalUnread += unread;
    totalMentions += mentions;
  }
  return { totalUnread, totalMentions, channelCount };
}

export function useGlobalUnreadCounts(
  selectedChannel: string | null,
): GlobalUnreadCounts {
  const [tree] = useSidebarTree();
  return useMemo(() => {
    if (!tree) return ZERO_COUNTS;
    return aggregateGlobalUnreadCounts(
      tree.channelMap,
      tree.mutedSet,
      tree.unreadMap,
      tree.mentionMap,
      selectedChannel,
    );
  }, [tree, selectedChannel]);
}
